type OrderItem = {
  quantity: number;
  containerType: string | null;
  volume: number | null;
  unit: string | null;
  packQuantity: number | null;
};

type CaseSizeRule = {
  quantity: number;
  containerType: "can" | "bottle";
  volume: number;
  unit: string;
};

type CaseSizeRuleKey = string;

const createKey = (
  item: Pick<OrderItem, "containerType" | "unit" | "volume">,
) => {
  if (!item.containerType || !item.unit || !item.volume) {
    return null;
  }

  return `${item.containerType}-${item.unit}-${item.volume}`;
};

export const filterPartiallySatisfiedCaseSizeRules = ({
  orderItemList,
  caseSizeRuleList,
}: {
  orderItemList: OrderItem[];
  caseSizeRuleList: CaseSizeRule[];
}) => {
  // case size rule group

  const caseSizeRuleMap = new Map<
    CaseSizeRuleKey,
    { caseSizeRule: CaseSizeRule; unitItemCount: number }
  >();

  for (const rule of caseSizeRuleList) {
    const caseSizeRuleKey = createKey(rule);

    if (!caseSizeRuleKey) {
      continue;
    }

    caseSizeRuleMap.set(caseSizeRuleKey, {
      caseSizeRule: rule,
      unitItemCount: 0,
    });
  }

  for (const orderItem of orderItemList) {
    const orderItemKey = createKey(orderItem);

    if (!orderItemKey) {
      continue;
    }

    // find applicable case size rule, if so update

    const caseSizeRuleFound = caseSizeRuleMap.get(orderItemKey);

    if (caseSizeRuleFound) {
      const orderItemPackQuantity = orderItem.packQuantity ?? 0;
      const currentUnitItemCount =
        caseSizeRuleMap.get(orderItemKey)?.unitItemCount ?? 0;

      const newUnitItemCount =
        currentUnitItemCount + orderItemPackQuantity * orderItem.quantity;

      caseSizeRuleMap.set(orderItemKey, {
        ...caseSizeRuleFound,
        unitItemCount: newUnitItemCount,
      });
      continue;
    }
  }

  const decoratedCaseSizeRules = Array.from(
    caseSizeRuleMap,
    ([, { caseSizeRule, unitItemCount }]) => {
      const caseSizeRuleSatisfied = !!Number.isInteger(
        unitItemCount / caseSizeRule.quantity,
      );
      return { caseSizeRule, satisfied: caseSizeRuleSatisfied };
    },
  );

  const partiallySatisfiedCaseSizeRules = decoratedCaseSizeRules
    .filter((rule) => !rule.satisfied)
    .map((rule) => rule.caseSizeRule);

  return partiallySatisfiedCaseSizeRules;
};
